import React, { useState, useEffect } from 'react';
import { ChatBubbleLeftRightIcon, PencilIcon, TrashIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { api } from '../../lib/supabase';
import { LOComment, formatRelativeDate } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface LOCommentsProps {
  loanOfficerId: string;
}

const LOComments: React.FC<LOCommentsProps> = ({ loanOfficerId }) => {
  const { loanOfficer: currentUser } = useAuth();
  const [comments, setComments] = useState<LOComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  // Fetch comments
  const fetchComments = async () => {
    try {
      setLoading(true);
      const data = await api.getLOComments(loanOfficerId);
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanOfficerId]);

  // Submit new comment
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser) return;

    setSubmitting(true);
    try {
      await api.createLOComment({
        loan_officer_id: loanOfficerId,
        author_id: currentUser.id,
        author_name: `${currentUser.first_name} ${currentUser.last_name}`,
        author_role: currentUser.user_role === 'va' ? 'va' : 'admin',
        content: newComment.trim(),
      });
      setNewComment('');
      await fetchComments();
    } catch (error) {
      console.error('Error creating comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Submit reply
  const handleReply = async (parentId: string) => {
    if (!replyContent.trim() || !currentUser) return;

    setSubmitting(true);
    try {
      await api.createLOComment({
        loan_officer_id: loanOfficerId,
        author_id: currentUser.id,
        author_name: `${currentUser.first_name} ${currentUser.last_name}`,
        author_role: currentUser.user_role === 'va' ? 'va' : 'admin',
        content: replyContent.trim(),
        parent_id: parentId,
      });
      setReplyingTo(null);
      setReplyContent('');
      await fetchComments();
    } catch (error) {
      console.error('Error creating reply:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Update comment
  const handleUpdate = async (commentId: string) => {
    if (!editContent.trim()) return;

    setSubmitting(true);
    try {
      await api.updateLOComment(commentId, editContent.trim());
      setEditingId(null);
      setEditContent('');
      await fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete comment
  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await api.deleteLOComment(commentId);
      await fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  // Render a single comment
  const renderComment = (comment: LOComment, isReply = false) => {
    const isAuthor = currentUser?.id === comment.author_id;
    const isEditing = editingId === comment.id;

    return (
      <div
        key={comment.id}
        className={`${isReply ? 'ml-8 border-l-2 border-slate-200 pl-4' : ''}`}
      >
        <div className="flex items-start gap-3 py-3">
          {/* Avatar */}
          <div
            className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
              comment.author_role === 'admin' ? 'bg-lumin-primary' : 'bg-purple-500'
            }`}
          >
            {comment.author_name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-medium text-slate-900">{comment.author_name}</span>
              <span
                className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                  comment.author_role === 'admin'
                    ? 'bg-lumin-primary/10 text-lumin-primary'
                    : 'bg-purple-100 text-purple-700'
                }`}
              >
                {comment.author_role.toUpperCase()}
              </span>
              <span className="text-xs text-slate-400">{formatRelativeDate(comment.created_at)}</span>
              {comment.is_edited && <span className="text-xs text-slate-400 italic">(edited)</span>}
            </div>

            {isEditing ? (
              <div className="mt-2">
                <textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent resize-none"
                  rows={3}
                  autoFocus
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => handleUpdate(comment.id)}
                    disabled={submitting || !editContent.trim()}
                    className="px-3 py-1.5 text-xs font-medium bg-lumin-primary text-white rounded-lg hover:bg-lumin-primary/90 disabled:opacity-50"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditContent('');
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.content}</p>
            )}

            {/* Actions */}
            {!isEditing && (
              <div className="flex items-center gap-3 mt-2">
                {!isReply && (
                  <button
                    onClick={() => {
                      setReplyingTo(replyingTo === comment.id ? null : comment.id);
                      setReplyContent('');
                    }}
                    className="text-xs text-slate-500 hover:text-lumin-primary"
                  >
                    Reply
                  </button>
                )}
                {isAuthor && (
                  <>
                    <button
                      onClick={() => {
                        setEditingId(comment.id);
                        setEditContent(comment.content);
                      }}
                      className="text-xs text-slate-500 hover:text-lumin-primary flex items-center gap-1"
                    >
                      <PencilIcon className="h-3 w-3" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(comment.id)}
                      className="text-xs text-slate-500 hover:text-rose-500 flex items-center gap-1"
                    >
                      <TrashIcon className="h-3 w-3" />
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}

            {/* Reply Input */}
            {replyingTo === comment.id && (
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleReply(comment.id);
                    }
                  }}
                  autoFocus
                />
                <button
                  onClick={() => handleReply(comment.id)}
                  disabled={submitting || !replyContent.trim()}
                  className="px-3 py-2 bg-lumin-primary text-white rounded-lg hover:bg-lumin-primary/90 disabled:opacity-50"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                  }}
                  className="px-3 py-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-1">
            {comment.replies.map((reply) => renderComment(reply, true))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 rounded-full border-2 border-slate-200 border-t-lumin-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-lumin-primary" />
          <h3 className="text-base font-semibold text-slate-900">Internal Notes</h3>
          <span className="text-xs text-slate-500">({comments.length} {comments.length === 1 ? 'note' : 'notes'})</span>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Staff notes about this loan officer. Only visible to Admins and VAs.
        </p>
      </div>

      {/* Comment List */}
      <div className="px-5 divide-y divide-slate-100">
        {comments.length === 0 ? (
          <div className="py-8 text-center">
            <ChatBubbleLeftRightIcon className="h-10 w-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No notes yet. Add the first one below.</p>
          </div>
        ) : (
          comments.map((comment) => renderComment(comment))
        )}
      </div>

      {/* New Comment Input */}
      <form onSubmit={handleSubmit} className="px-5 py-4 border-t border-slate-200 bg-slate-50">
        <div className="flex gap-3">
          <div
            className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
              currentUser?.user_role === 'va' ? 'bg-purple-500' : 'bg-lumin-primary'
            }`}
          >
            {currentUser?.first_name?.[0]}
            {currentUser?.last_name?.[0]}
          </div>
          <div className="flex-1">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Add a note about this loan officer..."
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-lumin-primary focus:border-transparent resize-none"
              rows={2}
            />
            <div className="flex justify-end mt-2">
              <button
                type="submit"
                disabled={submitting || !newComment.trim()}
                className="px-4 py-2 text-sm font-medium bg-lumin-primary text-white rounded-lg hover:bg-lumin-primary/90 disabled:opacity-50 flex items-center gap-2"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
                Post Note
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default LOComments;
